import {
  ArrowLeft,
  CircleStop,
  FileText,
  Gauge,
  Headphones,
  LockKeyhole,
  MessageSquareText,
  Mic,
  MicOff,
  PanelRightClose,
  PanelRightOpen,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  Send,
  Settings2,
  ShieldCheck,
  Square,
  Volume2
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useRoute } from "../lib/router";
import type { ConversationMessage, LearningSession, SceneAction } from "@shared/types";
import { Loader } from "../components/Common";
import { AuraWebGLScene } from "../components/AuraWebGLScene";
import { api } from "../lib/api";
import { languages, speedOptions } from "../lib/constants";
import { buildScenarioOpeningPreview } from "../lib/scenarioOpening";

type MicrophoneState = "idle" | "requesting" | "listening";
type MicrophonePermissionStatus = "unknown" | "requesting" | "granted" | "denied" | "unsupported";

function microphoneErrorMessage(error: unknown) {
  const name = error instanceof DOMException ? error.name : "";
  if (!window.isSecureContext) return "O microfone exige HTTPS. Publique o PWA em HTTPS ou use localhost.";
  if (name === "NotAllowedError" || name === "SecurityError") return "Permissão do microfone bloqueada. Clique no cadeado do navegador, permita o microfone e tente novamente.";
  if (name === "NotFoundError") return "Nenhum microfone foi encontrado neste dispositivo.";
  if (name === "NotReadableError" || name === "AbortError") return "O microfone está sendo usado por outro aplicativo. Feche-o e tente novamente.";
  return "Não foi possível acessar o microfone. Verifique a permissão do navegador e tente novamente.";
}

function recognitionErrorMessage(code: string) {
  const messages: Record<string, string> = {
    "not-allowed": "O navegador bloqueou o microfone. Clique no cadeado ao lado do endereço e marque Microfone como Permitir.",
    "service-not-allowed": "O serviço de reconhecimento de voz do navegador está bloqueado. Use Chrome ou Edge atualizado.",
    "audio-capture": "O navegador não conseguiu capturar áudio do microfone.",
    "no-speech": "Não ouvi nenhuma fala. Aproxime-se do microfone e tente novamente.",
    network: "O reconhecimento de voz do navegador ficou sem conexão. O chat por texto continua disponível.",
    aborted: "A escuta foi interrompida."
  };
  return messages[code] || `Falha no reconhecimento de voz (${code}).`;
}

export default function Conversation() {
  const [, params] = useRoute("/conversation/:id");
  const [, navigate] = useLocation();
  const id = Number(params?.id);
  const [session, setSession] = useState<LearningSession | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [microphoneState, setMicrophoneState] = useState<MicrophoneState>("idle");
  const [microphonePermission, setMicrophonePermission] = useState<MicrophonePermissionStatus>("unknown");
  const [microphonePromptVisible, setMicrophonePromptVisible] = useState(true);
  const [voiceMode, setVoiceMode] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [speakingSide, setSpeakingSide] = useState<"user" | "ai" | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const messagesEnd = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const recognition = useRef<SpeechRecognition | null>(null);
  const recognitionTimeout = useRef<number | null>(null);
  const suppressRecognitionError = useRef(false);
  const receivedFinalTranscript = useRef(false);
  const microphonePermissionChecked = useRef(false);
  const voiceModeRef = useRef(false);
  const sessionRef = useRef<LearningSession | null>(null);
  const startedAt = useRef(Date.now());

  const listening = microphoneState === "listening";
  const speed = useMemo(() => speedOptions.find((item) => item.value === session?.speed) || speedOptions[3], [session]);
  const lastAssistant = useMemo(() => [...messages].reverse().find((message) => message.speaker === "assistant") || null, [messages]);
  const lastUser = useMemo(() => [...messages].reverse().find((message) => message.speaker === "user") || null, [messages]);
  const lastAssistantText = lastAssistant?.text || "";
  const lastUserText = lastUser?.text || text;

  const applySession = useCallback((next: LearningSession) => {
    setSession(next);
    sessionRef.current = next;
  }, []);

  const ensureActiveSession = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) throw new Error("A sessão ainda não foi carregada.");
    if (current.status === "completed") throw new Error("Esta sessão já foi concluída.");
    if (current.status !== "paused") return current;

    const result = await api<{ session: LearningSession }>(`/api/sessions/${current.id}/resume`, { method: "POST" });
    applySession(result.session);
    setNotice("Sessão retomada com o mesmo provedor e o mesmo modelo.");
    setError("");
    return result.session;
  }, [applySession]);

  const stopListening = useCallback((silent = false) => {
    if (recognitionTimeout.current !== null) {
      window.clearTimeout(recognitionTimeout.current);
      recognitionTimeout.current = null;
    }
    if (recognition.current && silent) suppressRecognitionError.current = true;
    try { recognition.current?.abort(); } catch { /* reconhecimento já encerrado */ }
    recognition.current = null;
    setMicrophoneState("idle");
    setSpeakingSide(null);
  }, []);

  const stopVoiceMode = useCallback(() => {
    voiceModeRef.current = false;
    setVoiceMode(false);
    stopListening(true);
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setSpeakingSide(null);
  }, [stopListening]);

  const requestMicrophonePermission = useCallback(async () => {
    if (microphonePermissionChecked.current) {
      setMicrophonePermission("granted");
      return;
    }
    if (!window.isSecureContext) {
      setMicrophonePermission("unsupported");
      throw new DOMException("Secure context required", "SecurityError");
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicrophonePermission("unsupported");
      throw new DOMException("getUserMedia unavailable", "NotSupportedError");
    }
    setMicrophonePermission("requesting");
    setMicrophoneState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false
      });
      stream.getTracks().forEach((track) => track.stop());
      microphonePermissionChecked.current = true;
      setMicrophonePermission("granted");
      window.sessionStorage.setItem("linguahome_microphone_enabled", "true");
      setMicrophonePromptVisible(false);
      setNotice("Microfone habilitado. Clique em Falar uma vez ou Iniciar voz quando estiver pronto.");
      setError("");
    } catch (cause) {
      const denied = cause instanceof DOMException && (cause.name === "NotAllowedError" || cause.name === "SecurityError");
      setMicrophonePermission(denied ? "denied" : "unknown");
      throw cause;
    } finally {
      setMicrophoneState("idle");
    }
  }, []);

  const enableMicrophoneForLesson = useCallback(async () => {
    try {
      await requestMicrophonePermission();
    } catch (cause) {
      setError(cause instanceof DOMException ? microphoneErrorMessage(cause) : cause instanceof Error ? cause.message : "Não foi possível habilitar o microfone.");
    }
  }, [requestMicrophonePermission]);

  const speak = useCallback((value: string, continueConversation = false) => {
    const current = sessionRef.current;
    if (!current || !value.trim()) return;
    if (!("speechSynthesis" in window) || typeof SpeechSynthesisUtterance === "undefined") {
      setError("A síntese de voz não está disponível neste navegador. O chat por texto continua funcionando.");
      if (continueConversation && voiceModeRef.current) void startRecognitionRef.current(false);
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(value);
    const selectedSpeed = speedOptions.find((item) => item.value === current.speed) || speedOptions[3];
    utterance.lang = current.locale || languages[current.targetLanguage].speech;
    utterance.rate = selectedSpeed.rate;
    utterance.pitch = 1;
    utterance.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    utterance.voice = voices.find((voice) => voice.lang.toLowerCase() === utterance.lang.toLowerCase())
      || voices.find((voice) => voice.lang.toLowerCase().startsWith(utterance.lang.toLowerCase().slice(0, 2)))
      || null;
    utterance.onstart = () => {
      setError("");
      setSpeakingSide("ai");
    };
    utterance.onboundary = () => setSpeakingSide("ai");
    utterance.onend = () => {
      setSpeakingSide(null);
      if (continueConversation && voiceModeRef.current) window.setTimeout(() => void startRecognitionRef.current(false), 320);
    };
    utterance.onerror = (event) => {
      setSpeakingSide(null);
      if (event.error === "interrupted" || event.error === "canceled") return;
      setError("A voz do dispositivo não pôde ser reproduzida. Clique em Repetir ou continue pelo chat.");
      if (continueConversation && voiceModeRef.current) window.setTimeout(() => void startRecognitionRef.current(false), 120);
    };
    window.speechSynthesis.speak(utterance);
  }, []);

  const requestOpening = useCallback(async (baseSession: LearningSession) => {
    setOpening(true);
    const preview = buildScenarioOpeningPreview(baseSession);
    setMessages((current) => current.length ? current : [preview]);
    window.setTimeout(() => speak(preview.text, false), 180);
    try {
      const result = await api<{ session: LearningSession; message: ConversationMessage }>(`/api/sessions/${baseSession.id}/opening`, { method: "POST" });
      applySession(result.session || baseSession);
      setMessages((current) => {
        const previewIndex = current.findIndex((message) => message.id === preview.id);
        if (previewIndex < 0) return current.length ? current : [result.message];
        const next = [...current];
        next[previewIndex] = result.message;
        return next;
      });
      setError("");
    } catch (cause) {
      setNotice("A apresentação do cenário foi exibida. Você já pode conversar por voz ou texto.");
      setError(cause instanceof Error ? cause.message : "Não foi possível persistir a apresentação inicial.");
    } finally {
      setOpening(false);
    }
  }, [applySession, speak]);

  const load = useCallback(async () => {
    if (!Number.isFinite(id) || id <= 0) {
      setError("Identificador de sessão inválido.");
      setLoading(false);
      return;
    }
    try {
      const [result, providerResult] = await Promise.all([
        api<{ session: LearningSession; messages: ConversationMessage[] }>(`/api/sessions/${id}`),
        api<{ providers: AIProviderOption[] }>("/api/ai/providers")
      ]);
      let loadedSession = result.session;
      const configuredOnServer = Boolean(providerResult.providers.find((provider) => provider.id === loadedSession.aiProvider)?.configured);
      if (loadedSession.status === "paused" && configuredOnServer) {
        try {
          const resumed = await api<{ session: LearningSession }>(`/api/sessions/${id}/resume`, { method: "POST" });
          loadedSession = resumed.session;
          setNotice("A sessão anterior foi retomada com o mesmo provedor e modelo configurado no servidor.");
        } catch (resumeError) {
          setError(resumeError instanceof Error ? resumeError.message : "Não foi possível retomar a sessão.");
        }
      } else if (loadedSession.status === "paused" && !configuredOnServer) {
        const variable = loadedSession.aiProvider === "groq" ? "GROQ_API_KEY" : "GEMINI_API_KEY";
        setNotice(`O provedor desta sessão não está configurado no servidor. Defina ${variable} no arquivo .env e reinicie a aplicação.`);
      }
      applySession(loadedSession);
      setMessages(result.messages);
      setLoading(false);
      if (!result.messages.length && loadedSession.status === "active") void requestOpening(loadedSession);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sessão indisponível.");
      setLoading(false);
    }
  }, [applySession, id, requestOpening]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => {
      window.clearInterval(timer);
      if (recognitionTimeout.current !== null) window.clearTimeout(recognitionTimeout.current);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      try { recognition.current?.abort(); } catch { /* já finalizado */ }
    };
  }, [load]);

  useEffect(() => {
    if (window.sessionStorage.getItem("linguahome_microphone_enabled") === "true") {
      microphonePermissionChecked.current = true;
      setMicrophonePermission("granted");
      return;
    }
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setMicrophonePermission("unsupported");
      return;
    }
    if (!("permissions" in navigator)) return;
    void navigator.permissions.query({ name: "microphone" as PermissionName }).then((status) => {
      const sync = () => {
        if (status.state === "granted") {
          microphonePermissionChecked.current = true;
          setMicrophonePermission("granted");
        } else if (status.state === "denied") setMicrophonePermission("denied");
        else setMicrophonePermission("unknown");
      };
      sync();
      status.onchange = sync;
    }).catch(() => undefined);
  }, []);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, chatVisible]);
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);
  useEffect(() => {
    if (!chatVisible) return;
    const timer = window.setTimeout(() => textAreaRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [chatVisible]);

  const startRecognition = useCallback(async (singleShot = false) => {
    if (microphoneState !== "idle" || sending) return;
    try {
      const current = await ensureActiveSession();
      if (!singleShot && !voiceModeRef.current) return;
      const Constructor = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!Constructor) {
        if (!singleShot) stopVoiceMode();
        throw new Error("Este navegador não oferece reconhecimento de voz. Use Chrome ou Edge atualizado, ou converse pelo chat.");
      }

      await requestMicrophonePermission();
      // Aguarda o navegador liberar o dispositivo usado na verificação de
      // permissão antes de iniciar o SpeechRecognition.
      await new Promise((resolve) => window.setTimeout(resolve, 160));
      stopListening(true);
      receivedFinalTranscript.current = false;
      suppressRecognitionError.current = false;
      const instance = new Constructor();
      instance.lang = current.locale || languages[current.targetLanguage].speech;
      instance.interimResults = true;
      // No modo “Falar uma vez”, manter a captura aberta até a fala final ou
      // até o limite de segurança. Isso evita o desligamento quase imediato
      // após uma pequena pausa do aluno.
      instance.continuous = singleShot;
      instance.maxAlternatives = 1;
      instance.onstart = () => {
        setError("");
        setNotice(singleShot ? "Microfone ativo. Fale sua resposta; a escuta termina após uma pausa." : "Fale agora. AURA está ouvindo.");
        setMicrophoneState("listening");
        setSpeakingSide("user");
        recognitionTimeout.current = window.setTimeout(() => {
          if (recognition.current === instance) {
            setNotice("Tempo de escuta encerrado. Clique em Falar uma vez para tentar novamente.");
            try { instance.stop(); } catch { /* reconhecimento já encerrado */ }
          }
        }, singleShot ? 15000 : 20000);
      };
      instance.onresult = (event) => {
        let transcript = "";
        let final = false;
        for (let index = 0; index < event.results.length; index++) {
          transcript += event.results[index][0].transcript;
          final ||= event.results[index].isFinal;
        }
        setText(transcript);
        if (final && transcript.trim()) {
          receivedFinalTranscript.current = true;
          if (recognitionTimeout.current !== null) {
            window.clearTimeout(recognitionTimeout.current);
            recognitionTimeout.current = null;
          }
          try { instance.stop(); } catch { /* já encerrado */ }
          recognition.current = null;
          setMicrophoneState("idle");
          setSpeakingSide("user");
          void sendRef.current(transcript, true);
        }
      };
      instance.onerror = (event) => {
        recognition.current = null;
        setMicrophoneState("idle");
        setSpeakingSide(null);
        if (event.error === "aborted" && suppressRecognitionError.current) {
          suppressRecognitionError.current = false;
          return;
        }
        const message = recognitionErrorMessage(event.error);
        if (event.error === "no-speech") {
          setNotice("Nenhuma fala foi detectada. Clique em Falar uma vez e comece a falar quando o indicador ficar ativo.");
          setError("");
        } else setError(message);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") setMicrophonePermission("denied");
        if (!singleShot && event.error !== "no-speech") stopVoiceMode();
      };
      instance.onend = () => {
        if (recognitionTimeout.current !== null) {
          window.clearTimeout(recognitionTimeout.current);
          recognitionTimeout.current = null;
        }
        recognition.current = null;
        setMicrophoneState("idle");
        setSpeakingSide(null);
        suppressRecognitionError.current = false;
        if (!receivedFinalTranscript.current && singleShot) {
          setNotice("A escuta terminou sem uma frase completa. Clique em Falar uma vez e fale depois que o indicador mostrar ‘Microfone ativo’.");
        }
        if (!receivedFinalTranscript.current && !singleShot && voiceModeRef.current) {
          setNotice("AURA continua aguardando. Clique em Falar uma vez ou abra o chat.");
        }
      };
      recognition.current = instance;
      instance.start();
    } catch (cause) {
      setMicrophoneState("idle");
      setSpeakingSide(null);
      const message = cause instanceof Error && cause.message ? cause.message : microphoneErrorMessage(cause);
      setError(cause instanceof DOMException ? microphoneErrorMessage(cause) : message);
      if (!singleShot) stopVoiceMode();
    }
  }, [ensureActiveSession, microphoneState, requestMicrophonePermission, sending, stopListening, stopVoiceMode]);

  const startRecognitionRef = useRef(startRecognition);
  useEffect(() => { startRecognitionRef.current = startRecognition; }, [startRecognition]);

  const send = useCallback(async (value = text, fromVoice = false) => {
    const clean = value.trim();
    if (!clean || sending) return;
    try {
      const current = await ensureActiveSession();
      if (!fromVoice) {
        stopListening(true);
        if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      }
      setSending(true);
      setText("");
      setError("");
      setNotice("");
      setSpeakingSide("user");
      const result = await api<{ userMessage: ConversationMessage; assistantMessage: ConversationMessage; sceneAction: SceneAction }>(`/api/sessions/${current.id}/messages`, {
        method: "POST",
        body: JSON.stringify({ text: clean })
      });
      setMessages((old) => [...old, result.userMessage, result.assistantMessage]);
      const nextSession = { ...current, sceneAction: result.sceneAction, status: "active" as const, aiStatus: "ready" as const, pausedReason: null };
      applySession(nextSession);
      speak(result.assistantMessage.text, voiceModeRef.current);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Erro ao responder.";
      stopVoiceMode();
      setSpeakingSide(null);
      setError(message);
      setText(clean);
      const current = sessionRef.current;
      if (current) applySession({ ...current, status: "paused", aiStatus: "error", pausedReason: message });
    } finally {
      setSending(false);
    }
  }, [applySession, ensureActiveSession, sending, speak, stopListening, stopVoiceMode, text]);

  const sendRef = useRef(send);
  useEffect(() => { sendRef.current = send; }, [send]);

  const startVoiceMode = useCallback(async () => {
    try {
      await ensureActiveSession();
      await requestMicrophonePermission();
      setError("");
      setNotice("Modo de voz iniciado. AURA falará e depois abrirá o microfone.");
      voiceModeRef.current = true;
      setVoiceMode(true);
      const last = [...messages].reverse().find((message) => message.speaker === "assistant");
      if (last) speak(last.text, true);
      else window.setTimeout(() => void startRecognitionRef.current(false), 120);
    } catch (cause) {
      setError(cause instanceof DOMException ? microphoneErrorMessage(cause) : cause instanceof Error ? cause.message : "Não foi possível iniciar a voz.");
      stopVoiceMode();
    }
  }, [ensureActiveSession, messages, requestMicrophonePermission, speak, stopVoiceMode]);

  const repeatLast = useCallback(() => {
    const last = [...messages].reverse().find((message) => message.speaker === "assistant");
    if (last) {
      setError("");
      speak(last.text, false);
    } else setError("AURA ainda não possui uma fala para repetir.");
  }, [messages, speak]);

  const updatePreference = async (field: "speed" | "correctionMode", value: string) => {
    if (!session) return;
    try {
      const result = await api<{ session: LearningSession }>(`/api/sessions/${id}/preferences`, {
        method: "PATCH",
        body: JSON.stringify({ [field]: value })
      });
      applySession(result.session);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Erro ao atualizar preferência.");
    }
  };

  const resume = async () => {
    try { await ensureActiveSession(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Não foi possível retomar."); }
  };

  const finish = async () => {
    stopVoiceMode();
    const minutes = Math.max(1, Math.ceil(elapsed / 60));
    try {
      await api(`/api/sessions/${id}/complete`, { method: "POST", body: JSON.stringify({ minutes }) });
      navigate(`/report/${id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Erro ao finalizar.");
    }
  };

  if (loading) return <div className="conversation-loading"><Loader full /></div>;
  if (!session) return <div className="conversation-error"><p>{error}</p><Link href="/dashboard">Voltar</Link></div>;

  const readonly = session.status === "completed";
  return <div className="conversation-page">
    {!readonly && microphonePromptVisible && <div className="microphone-permission-backdrop" role="dialog" aria-modal="true" aria-labelledby="microphone-permission-title">
      <div className="microphone-permission-dialog">
        <div className="microphone-permission-icon"><Mic /></div>
        <span className="microphone-permission-kicker">Antes de começar a aula</span>
        <h2 id="microphone-permission-title">Habilite o microfone para conversar com AURA</h2>
        <p>O navegador pedirá sua autorização. O áudio é usado somente durante a prática de voz. O chat por texto continua opcional e pode permanecer fechado.</p>
        <div className={`microphone-permission-status ${microphonePermission}`}>
          {microphonePermission === "granted" && <><ShieldCheck /> Microfone já autorizado neste navegador.</>}
          {microphonePermission === "requesting" && <><Loader /> Aguardando sua confirmação no navegador...</>}
          {microphonePermission === "denied" && <><MicOff /> Permissão bloqueada. Use o cadeado ao lado do endereço para permitir.</>}
          {microphonePermission === "unsupported" && <><MicOff /> O microfone exige HTTPS e um navegador compatível.</>}
          {microphonePermission === "unknown" && <><Mic /> Clique abaixo e confirme a permissão quando o navegador perguntar.</>}
        </div>
        <div className="microphone-permission-actions">
          {microphonePermission === "granted"
            ? <button className="primary-button" type="button" onClick={() => setMicrophonePromptVisible(false)}><PlayCircle /> Continuar aula</button>
            : <button className="primary-button" type="button" onClick={() => void enableMicrophoneForLesson()} disabled={microphonePermission === "requesting"}><Mic /> {microphonePermission === "requesting" ? "Aguardando permissão..." : "Habilitar microfone"}</button>}
          <button className="secondary-button" type="button" onClick={() => {
            setMicrophonePromptVisible(false);
            setNotice("Você entrou sem habilitar o microfone. É possível ativá-lo depois em Iniciar voz ou Falar uma vez.");
          }}>Continuar sem microfone</button>
        </div>
        <small><LockKeyhole /> A habilitação do microfone não altera o provedor de IA e não ativa fallback.</small>
      </div>
    </div>}
    <header className="conversation-header">
      <Link href="/dashboard" className="icon-button"><ArrowLeft /></Link>
      <div className="conversation-title">
        <span>{session.scenarioIcon}</span>
        <div><h1>{session.scenarioTitle}</h1><p>{languages[session.targetLanguage].flag} Você é {session.userRole} · AURA atua como {session.aiRole}</p></div>
      </div>
      <div className="conversation-meta">
        <label className="inline-control"><Gauge /><select value={session.speed} onChange={(event) => void updatePreference("speed", event.target.value)} disabled={readonly}>{speedOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <span className="provider-lock"><LockKeyhole /> {session.aiProvider === "groq" ? "GroqCloud" : "Google Gemini"} · sem fallback</span>
        <button className="secondary-button chat-toggle" type="button" onClick={() => setChatVisible((value) => !value)}>
          {chatVisible ? <PanelRightClose /> : <PanelRightOpen />}{chatVisible ? "Ocultar chat" : "Mostrar chat"}
        </button>
        {readonly
          ? <Link href={`/report/${id}`} className="secondary-button"><FileText /> Relatório</Link>
          : <button className="danger-button" onClick={finish}><Square /> Finalizar</button>}
      </div>
    </header>

    <div className={`immersive-conversation-body ${chatVisible ? "chat-open" : "chat-collapsed"}`}>
      <section className="visual-dialogue-panel">
        <AuraWebGLScene
          session={session}
          speakingSide={speakingSide}
          listening={listening}
          assistantText={lastAssistantText}
          userText={lastUserText}
          emotion={lastAssistant?.emotion}
        />

        {!readonly && <div className="voice-control-card voice-control-expanded">
          <div><Headphones /><span><b>Conversa natural por voz</b><small>{opening ? "AURA está registrando a apresentação do cenário." : microphoneState === "requesting" ? "Solicitando permissão do microfone..." : microphonePermission === "granted" ? "Microfone habilitado; a voz é principal e o chat permanece opcional." : "Habilite o microfone para falar com AURA; o chat permanece opcional."}</small></span></div>
          <div className="voice-buttons">
            {voiceMode
              ? <button className="danger-button" onClick={stopVoiceMode}><CircleStop /> Parar voz</button>
              : <button className="primary-button" onClick={() => void startVoiceMode()}><PlayCircle /> Iniciar voz</button>}
            <button className="secondary-button" onClick={() => listening ? stopListening(true) : void startRecognition(true)} disabled={sending || microphoneState === "requesting"}>
              {listening ? <MicOff /> : <Mic />}{listening ? "Parar" : microphoneState === "requesting" ? "Permitindo..." : "Falar uma vez"}
            </button>
            <button className="secondary-button" onClick={repeatLast}><RotateCcw /> Repetir</button>
            <button className="secondary-button" onClick={() => setChatVisible((value) => !value)}><MessageSquareText /> {chatVisible ? "Fechar chat" : "Abrir chat"}</button>
          </div>
        </div>}

        {notice && !chatVisible && <div className="scene-inline-notice">{notice}</div>}
        {error && !chatVisible && <div className="scene-inline-error">{error}</div>}
        {session.status === "paused" && !chatVisible && <div className="scene-paused-card"><PauseCircle /><div><b>Sessão aguardando retomada</b><p>{session.pausedReason || error}</p></div><button onClick={() => void resume()}><PlayCircle /> Retomar</button></div>}
        <div className="session-lock-card"><ShieldCheck /><div><b>Provedor e modelo travados</b><span>{session.aiProvider === "groq" ? "GroqCloud" : "Google Gemini"} · {session.aiModel}. Se houver falha, o sistema mantém o mesmo modelo e não transfere a conversa para outra IA.</span></div></div>
      </section>

      {chatVisible && <section className="chat-panel immersive-chat">
        <div className="chat-toolbar">
          <div><b>Diálogo com AURA</b><span>{opening ? "Apresentação carregada; você já pode responder." : sending ? "A IA está respondendo..." : listening ? "Ouvindo você..." : voiceMode ? "Voz ativa e chat opcional aberto" : "Use texto, microfone ou os dois"}</span></div>
          <label><Settings2 /><select value={session.correctionMode} onChange={(event) => void updatePreference("correctionMode", event.target.value)} disabled={readonly}><option value="discreet">Correção discreta</option><option value="immediate">Correção imediata</option><option value="end">Correção no final</option></select></label>
        </div>
        <div className="conversation-channels-live"><span className={voiceMode ? "active" : ""}><Headphones /> Voz {voiceMode ? "ativa" : "principal"}</span><span className="active"><MessageSquareText /> Chat por texto opcional</span></div>
        <div className="chat-messages">
          {messages.map((message) => <article key={message.id} className={`message ${message.speaker}`}>
            <div className="message-avatar">{message.speaker === "assistant" ? "A" : "EU"}</div>
            <div className="message-content">
              <span className="message-author">{message.speaker === "assistant" ? `AURA · ${session.aiRole}` : "Você"}</span>
              <p>{message.text}</p>
              {message.translation && <small className="translation">Tradução: {message.translation}</small>}
              {message.correction && <small className="correction">Ajuste: {message.correction}</small>}
              <time>{new Date(message.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</time>
            </div>
            {message.speaker === "assistant" && <button className="message-audio" onClick={() => speak(message.text, false)} aria-label="Ouvir"><Volume2 /></button>}
          </article>)}
          {sending && <article className="message assistant typing"><div className="message-avatar">A</div><div className="message-content"><span className="message-author">AURA está pensando</span><span><i /><i /><i /></span></div></article>}
          <div ref={messagesEnd} />
        </div>

        {session.status === "paused" && !readonly && <div className="session-paused"><PauseCircle /><div><b>A conversa será retomada no mesmo modelo</b><p>{session.pausedReason || error || "O provedor selecionado não respondeu corretamente."}</p><small>Você pode continuar digitando; ao enviar, o sistema tentará retomar o mesmo provedor e modelo.</small></div><button onClick={() => void resume()}><PlayCircle /> Retomar</button></div>}
        {notice && <div className="chat-notice">{notice}</div>}
        {error && <div className="chat-error">{error}</div>}
        {!readonly && <>
          <div className="quick-actions">
            <button onClick={repeatLast}><RotateCcw /> Repetir</button>
            <button onClick={() => listening ? stopListening(true) : void startRecognition(true)} disabled={sending || microphoneState === "requesting"}>{listening ? <MicOff /> : <Mic />}{listening ? "Parar" : "Falar uma vez"}</button>
          </div>
          <div className="composer text-chat-composer">
            <textarea
              ref={textAreaRef}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              disabled={sending}
              placeholder={session.status === "paused" ? "Digite normalmente; ao enviar, a sessão será retomada no mesmo modelo..." : "Digite sua mensagem no idioma que está praticando..."}
              aria-label="Mensagem para AURA"
            />
            <button className={`mic-button ${listening ? "recording" : ""}`} onClick={() => listening ? stopListening(true) : void startRecognition(true)} disabled={sending || microphoneState === "requesting" || voiceMode} title={voiceMode ? "O modo de voz contínua já está ativo" : "Falar uma vez"}>{listening ? <MicOff /> : <Mic />}</button>
            <button className="send-button" onClick={() => void send()} disabled={!text.trim() || sending} title="Enviar mensagem"><Send /></button>
          </div>
          <small className="composer-help">Enter envia · Shift+Enter quebra a linha · o campo permanece disponível mesmo durante a apresentação inicial.</small>
        </>}
      </section>}
    </div>
  </div>;
}
